import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { UserInputResponse } from '@dreamai/types';
import api from '../../api';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import './new-project-modal.scss';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  initialTitle?: string;
  /** Slug of the project suggestion template (stored on the thread for directive lookup). */
  suggestionSlug?: string;
  onProjectCreated?: () => void;
}

const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
  agentId,
  initialTitle,
  suggestionSlug,
  onProjectCreated,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes; focus input when opening.
  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle ?? '');
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setTitle('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen, initialTitle]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    const name = title.trim();
    if (!name || submitting) return;

    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await api.post<UserInputResponse>(
        '/agents',
        {
          agent_id: agentId,
          message: `SYSTEM_MESSAGE The user will send you the first message in a minute they have named this thread ${name}. Send a short message appropriate to that thread name asking them what they want to discuss.\n          `,
          thread_name: name,
          creation_type: 'project',
          ...(suggestionSlug
            ? { project_suggestion_slug: suggestionSlug }
            : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.thread_id) {
        onProjectCreated?.();
        onClose();
        navigate(
          `/conversations?agent_id=${agentId}&thread_id=${response.data.thread_id}`,
        );
      } else {
        setError('Could not create project. Please try again.');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Could not create project. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className='new-project-modal-overlay' onClick={onClose}>
      <div
        className='new-project-modal'
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-labelledby='new-project-modal-title'
      >
        <h1 id='new-project-modal-title' className='new-project-modal-title'>
          New Project
        </h1>
        <p className='new-project-modal-subtitle'>
          Handover the big things to your Assistant.
        </p>
        <p className='new-project-modal-body'>
          Create your project and manage everything from holidays to orders,
          nursery planning to customer feedback. All with a custom Project App.
        </p>

        <div className='new-project-modal-field'>
          <label
            htmlFor='new-project-modal-input'
            className='new-project-modal-label'
          >
            Project title
          </label>
          <input
            ref={inputRef}
            id='new-project-modal-input'
            type='text'
            className='new-project-modal-input'
            placeholder='eg. Summer trip, or Appointment manager'
            value={title}
            maxLength={60}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={submitting}
          />
        </div>

        {error && <div className='new-project-modal-error'>{error}</div>}

        <div className='new-project-modal-actions'>
          <button
            type='button'
            className='new-project-modal-cancel'
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type='button'
            className='new-project-modal-submit'
            onClick={handleSubmit}
            disabled={submitting || !title.trim()}
          >
            <span>Start Project</span>
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewProjectModal;
